/**
 * Mapeamento de componentes de certificação para suas respectivas rotas
 */

export type CertificationComponentType = 
  | 'certification_show'
  | 'certification_modeling'
  | 'certification_to_do_start'
  | 'certification_to_do'
  | 'certification_report'
  | 'certification_feedback';

export interface CertificationRouteMapping {
  name: string;
  path: string;
}

/**
 * Mapa de componentes para rotas de certificação (sem id)
 */
const certificationRouteMap: Record<CertificationComponentType, CertificationRouteMapping> = {
  certification_show: {
    name: 'certifications-detail',
    path: '/certifications/:id'
  },
  certification_modeling: {
    name: 'certifications-modeling',
    path: '/certifications/:id/modeling'
  },
  certification_to_do_start: {
    name: 'certifications-to-do-start',
    path: '/certifications/:id/to-do-start'
  },
  certification_to_do: {
    name: 'certifications-to-do',
    path: '/certifications/:id/to-do'
  },
  certification_report: {
    name: 'certifications-sending-report',
    path: '/certifications/:id/sending-report'
  },
  certification_feedback: {
    name: 'certifications-feedback',
    path: '/certifications/:id/feedback'
  }
};

/**
 * Obtém a rota correspondente ao componente de certificação
 * @param component - Nome do componente de certificação
 * @returns Objeto com name e path da rota, ou null se não encontrado
 */
export const getCertificationRoute = (component: string): CertificationRouteMapping | null => {
  return certificationRouteMap[component as CertificationComponentType] || null;
};

/**
 * Obtém o nome da rota correspondente ao componente de certificação
 * @param component - Nome do componente de certificação
 * @returns Nome da rota ou null se não encontrado
 */
export const getCertificationRouteName = (component: string): string | null => {
  const route = getCertificationRoute(component);
  return route?.name || null;
};

/**
 * Obtém o path da rota correspondente ao componente de certificação
 * @param component - Nome do componente de certificação
 * @returns Path da rota ou null se não encontrado
 */
export const getCertificationRoutePath = (component: string): string | null => {
  const route = getCertificationRoute(component);
  return route?.path || null;
};

/**
 * Verifica se um componente de certificação é válido
 * @param component - Nome do componente a verificar
 * @returns true se o componente é válido, false caso contrário
 */
export const isValidCertificationComponent = (component: string): boolean => {
  return component in certificationRouteMap;
};

/**
 * Obtém o path da rota com o id da certificação substituído
 * @param component - Nome do componente de certificação
 * @param certificationId - ID da certificação
 * @returns Path da rota com id substituído ou null se não encontrado
 */
export const getCertificationRoutePathWithId = (component: string, certificationId: string | number): string | null => {
  const route = getCertificationRoute(component);
  if (!route) return null;
  return route.path.replace(':id', String(certificationId));
};
